import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import ReactJson from '@microlink/react-json-view'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import Typography from '@mui/material/Typography'
import { useAlert } from 'providers/AlertProvider'
import { useAuth } from 'providers/AuthProvider'
import { ResponsiveBoxWrapper } from 'components/Layout/Layout.styled'
import { usePatchPlace } from 'hooks/usePlaces'
import { useExtData } from 'hooks/useExternalData'
import GoBackButton from 'components/GoBackButton/GoBackButton'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import theme from 'lib/theme'
import { Form, Formik, FormikState } from 'formik'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import { PlaceSecondarySidebar } from './PlaceSecondarySidebar'

export const PlaceExtension = () => {
  const [showOverlay, setShowOverlay] = useState(true)
  const { setAlert } = useAlert()
  const { userStores } = useAuth()
  const { t } = useTranslation()
  const { placeId } = useParams()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { isLoading, error, data } = useExtData(placeId as string)
  const { mutateAsync, isLoading: isLoadingRequest } = usePatchPlace(
    placeId as string
  )
  const editable =
    userStores.includes(placeId as string) || userStores.includes('any')

  // reactjsonview copy does not work with mui otherwise
  const handleCopy = (copy: {
    src: object
    namespace: Array<string | null>
    name: string | null
  }) => {
    navigator.clipboard.writeText(JSON.stringify(copy.src, null, 2))
  }

  const handleSubmit = async (
    values: { externalData: Record<string, unknown> },
    resetForm: (
      nextState?: Partial<
        FormikState<{ externalData: Record<string, unknown> }>
      >
    ) => void
  ) => {
    const placeValues = {
      external_data: values.externalData,
    }
    await mutateAsync(placeValues, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        resetForm({ values })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  const initialValues = {
    externalData: data?.externalData ?? {},
  }

  if (isLoading) return <LoadingIndicator />
  if (error) return <h1>Error</h1>
  return (
    <Box sx={{ display: 'flex' }}>
      <PlaceSecondarySidebar
        placeId={placeId as string}
        placeName={data.name}
        placeMasterId={data.masterId}
      />
      <ResponsiveBoxWrapper showOverlay={showOverlay}>
        <Box
          maxWidth='500px'
          height='100%'
          display='flex'
          flexDirection='column'>
          {isMobile && <GoBackButton onClick={() => setShowOverlay(false)} />}
          <Typography mt={6} variant='body2'>
            {t('extensions')}
          </Typography>
          <Typography
            variant='body2'
            mt={2}
            ml={1}
            sx={{ color: theme.palette.text.secondary }}>
            {t('externalData')}
          </Typography>
          <Box
            mb={7}
            sx={{
              flex: 1,
              backgroundColor: theme.palette.neutral.grey12,
            }}>
            <Formik
              initialValues={initialValues}
              onSubmit={async (values, { resetForm }) =>
                handleSubmit(values, resetForm)
              }>
              {({ isValid, dirty, setFieldValue, values }) => (
                <Form>
                  <Box p={2} ml={1}>
                    <ReactJson
                      enableClipboard={handleCopy}
                      src={values.externalData}
                      theme='grayscale:inverted'
                      style={{
                        backgroundColor: theme.palette.neutral.grey12,
                      }}
                      onEdit={
                        editable
                          ? ({ updated_src: updatedSrc }) =>
                              setFieldValue('externalData', updatedSrc)
                          : false
                      }
                      onAdd={
                        editable
                          ? ({ updated_src: updatedSrc }) =>
                              setFieldValue('externalData', updatedSrc)
                          : false
                      }
                      onDelete={
                        editable
                          ? ({ updated_src: updatedSrc }) =>
                              setFieldValue('externalData', updatedSrc)
                          : false
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      position: 'fixed',
                      bottom: isMobile ? '0px' : theme.spacing(2),
                      width: isMobile ? '92%' : '500px',
                      zIndex: 1000,
                    }}>
                    <SaveBtn
                      showOverlay={showOverlay}
                      dirty={dirty}
                      isValid={isValid}
                      isLoadingRequest={isLoadingRequest}
                    />
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </ResponsiveBoxWrapper>
    </Box>
  )
}
